import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import { OverviewRankingsResponse, ReportingComparison } from '../../types/reporting';

interface RankingsRequest {
    startDate: string;
    endDate: string;
    locationId?: string;
    limit?: number;
    preset?: string;
    comparison?: ReportingComparison;
    includeComparison?: boolean;
    excludeEvs?: boolean;
}

export const getOverviewRankings = async (
    sessionId: string,
    input: RankingsRequest
): Promise<OverviewRankingsResponse> => {
    const { data } = await axios.post<OverviewRankingsResponse>(
        `${API_URL}/reporting/overview/rankings`,
        input,
        { headers: { Authorization: sessionId } }
    );

    // Ensure all arrays have defaults
    return {
        locations: data.locations || [],
        vendors: data.vendors || [],
        oems: data.oems || [],
        insurance: data.insurance || [],
        partTypes: data.partTypes || [],
    };
};

export const useOverviewRankings = (
    startDate: string,
    endDate: string,
    locationId?: string,
    limit: number = 10,
    preset?: string,
    comparison?: ReportingComparison,
    excludeEvs?: boolean,
    options?: { enabled?: boolean },
) => {
    const sessionContext = useContext(SessionContext);

    return useQuery({
        queryKey: ['reporting', 'overview', 'rankings', startDate, endDate, locationId, limit, preset, comparison, excludeEvs],
        queryFn: () => getOverviewRankings(sessionContext?.sessionId ?? '', {
            startDate,
            endDate,
            locationId,
            limit,
            preset,
            comparison,
            includeComparison: !!comparison,
            excludeEvs,
        }),
        enabled: !!sessionContext?.sessionId && !!startDate && !!endDate && (options?.enabled ?? true),
        staleTime: 1000 * 60 * 5,
        placeholderData: (previousData) => previousData,
    });
};
